import { StakingError, SubmitStakePayload } from '../saga/staking/types';
import { CurrentStakeType } from '../types';
import { GetState, ReduxAction, ThunkDispatchType } from 'app/redux/types';

import { BetErrorCode, MatchErrorCode, SubmitStakeType } from '../constants';
import { selectBigStakeByDialogId } from '../selectors/bigStakes';
import { selectBets } from '../selectors/entries';
import { selectCurrentStakeType } from '../selectors/stakeTypes';
import { selectSingleBetsInfo } from '../selectors/stakeTypes/single';
import { AvailabilityStakesDAO } from '../types/backend/availableStakes';
import { EntriesBets } from '../types/entries';
import { BetsInfo } from '../types/stakeTypes/singles';

// ---

export const PROCESS_STAKES = 'BET_SLIP:PROCESS_STAKES';

export type ProcessStakes = ReduxAction<typeof PROCESS_STAKES, {
  stakeType: CurrentStakeType;
  entries: EntriesBets;
  betsInfo: BetsInfo;
}>;

type ProcessStakesAction = (dispatch: ThunkDispatchType<ProcessStakes>, getState: GetState) => void;

export const processStakes = (): ProcessStakesAction => (dispatch, getState) => {
  const state = getState();

  return dispatch({
    type: PROCESS_STAKES,
    payload: {
      stakeType: selectCurrentStakeType(state),
      entries: selectBets(state),
      betsInfo: selectSingleBetsInfo(state),
    },
  });
};

// ---

export const PROCESS_STAKES_COMPLETE = 'BET_SLIP:PROCESS_STAKES_COMPLETE';

export type ProcessStakesComplete = ReduxAction<typeof PROCESS_STAKES_COMPLETE, {
  body: AvailabilityStakesDAO | null;
}>;

export const processStakesComplete = (
  body: AvailabilityStakesDAO | null,
): ProcessStakesComplete => ({
  type: PROCESS_STAKES_COMPLETE,
  payload: { body },
});

// ---

export const SUBMIT_STAKE_REQUEST = 'BET_SLIP:SUBMIT_STAKE_REQUEST';

type SubmitStakeRequestPayload = {
  type: SubmitStakeType;
  bets: {
    id: number;
    matchId: number;
    price: number;
  }[];
  amount: number;
  matchPrices?: Record<string, number>;
  dialogId?: string;
};

export type SubmitStakeRequest = ReduxAction<
  typeof SUBMIT_STAKE_REQUEST,
  SubmitStakeRequestPayload
>;

export const submitStakeRequest = (payload: SubmitStakeRequestPayload): SubmitStakeRequest => ({
  type: SUBMIT_STAKE_REQUEST,
  payload,
});

// ---

export const SUBMIT_STAKE_SUCCESS = 'BET_SLIP:SUBMIT_STAKE_SUCCESS';

export type SubmitStakeSuccessPayload = {
  dialogId: string;
  stakeId: number;
  winning: number;
  stakePrice: number;
  requestPayload?: SubmitStakePayload;
};

export type SubmitStakeSuccess = ReduxAction<
  typeof SUBMIT_STAKE_SUCCESS,
  SubmitStakeSuccessPayload & { isBigStake: boolean }
>;

export type SubmitStakeSuccessAction = (
  dispatch: ThunkDispatchType<SubmitStakeSuccess>,
  getState: GetState,
) => void;

export const submitStakeSuccess = (
  payload: SubmitStakeSuccessPayload,
): SubmitStakeSuccessAction => (dispatch, getState) => {
  const state = getState();

  const isBigStake = !!selectBigStakeByDialogId(state, payload.dialogId);

  dispatch({
    type: SUBMIT_STAKE_SUCCESS,
    payload: { ...payload, isBigStake },
  });
};

// ---

export const SUBMIT_STAKE_FAILURE = 'BET_SLIP:SUBMIT_STAKE_FAILURE';

export type MatchError = {
  matchId: number;
  code: MatchErrorCode;
  newPrice?: number;
};

export type BetError = {
  betId: number;
  matchId: number;
  code: BetErrorCode;
  newPrice?: number;
};

export type SubmitStakeFailurePayload = {
  dialogId: string;
  requestPayload?: SubmitStakeRequestPayload;
  matchErrors?: MatchError[];
  betErrors?: BetError[];
};

export type SubmitStakeFailure = ReduxAction<
  typeof SUBMIT_STAKE_FAILURE,
  SubmitStakeFailurePayload & { isBigStake: boolean }
> & { error: StakingError };

export type SubmitStakeFailureAction = (
  dispatch: ThunkDispatchType<SubmitStakeFailure>,
  getState: GetState,
) => void;

export const submitStakeFailure = (
  error: StakingError,
  payload: SubmitStakeFailurePayload,
): SubmitStakeFailureAction => (dispatch, getState) => {
  const state = getState();

  const isBigStake = !!selectBigStakeByDialogId(state, payload.dialogId);

  dispatch({
    type: SUBMIT_STAKE_FAILURE,
    error,
    payload: { ...payload, isBigStake },
  });
};

// ---

export const FAILURE_SUBMISSION_END = 'BET_SLIP:FAILURE_SUBMISSION_END';

export type FailureSubmissionEnd = ReduxAction<typeof FAILURE_SUBMISSION_END, {
  numberOfBets: number;
  hasPriceChangeError: boolean;
  betIds: number[];
}>;

export const failureSubmissionEnd = (
  numberOfBets: number,
  hasPriceChangeError: boolean,
  betIds: number[],
): FailureSubmissionEnd => ({
  type: FAILURE_SUBMISSION_END,
  payload: { numberOfBets, hasPriceChangeError, betIds },
});
